"use client"

import { useCallback, useEffect, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { cn } from "@/lib/utils"
import useEmblaCarousel from "embla-carousel-react"
import { ChevronLeft, ChevronRight, Waves, Trees, Dumbbell, Utensils, ShoppingBag, School } from "lucide-react"
import { Button } from "@/components/ui/button"
import type { Community } from "@/types/database"

interface CommunitiesShowcaseProps {
  readonly communities: Community[];
  readonly eyebrow?: string | null;
  readonly title?: string | null;
  readonly subtitle?: string | null;
  readonly description?: string | null;
}

export function CommunitiesShowcase({
  communities,
  eyebrow = "Our Communities",
  title = "Curated",
  subtitle = "Destinations",
  description = "Discover master-planned communities designed for an elevated lifestyle, blending architectural brilliance with world-class amenities.",
}: CommunitiesShowcaseProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 30 })
  const [selectedIndex, setSelectedIndex] = useState(0)

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    onSelect()
    emblaApi.on("select", onSelect)
  }, [emblaApi, onSelect])

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi])
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi])

  // Helper to map amenities to icons
  const getAmenityIcon = (amenity: string) => {
    const lower = amenity.toLowerCase()
    if (lower.includes("beach") || lower.includes("pool") || lower.includes("marina") || lower.includes("water")) return <Waves className="w-5 h-5 mb-2" />
    if (lower.includes("park") || lower.includes("green") || lower.includes("golf")) return <Trees className="w-5 h-5 mb-2" />
    if (lower.includes("gym") || lower.includes("fitness") || lower.includes("sport")) return <Dumbbell className="w-5 h-5 mb-2" />
    if (lower.includes("dining") || lower.includes("restaurant")) return <Utensils className="w-5 h-5 mb-2" />
    if (lower.includes("shop") || lower.includes("mall")) return <ShoppingBag className="w-5 h-5 mb-2" />
    if (lower.includes("school")) return <School className="w-5 h-5 mb-2" />
    return <Trees className="w-5 h-5 mb-2" /> // Default
  }

  if (communities.length === 0) {
    return null
  }

  return (
    <section className="relative w-full bg-background pt-12 pb-24">
      {/* Section Header */}
      <div className="container mx-auto px-6 md:px-12 mb-12 text-center space-y-8">
        <div className="space-y-4">
          <span className="text-xs uppercase tracking-[0.2em] text-secondary block font-bold">
            {eyebrow}
          </span>
          <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[0.9] text-primary">
            {title} <br /> <span className="italic text-primary/50">{subtitle}</span>
          </h2>
        </div>
        <p className="font-sans text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
          {description}
        </p>
      </div>

      <div className="container mx-auto px-6 md:px-12 h-[70vh] relative group">
        <div className="absolute inset-0 z-0 overflow-hidden rounded-xl shadow-2xl" ref={emblaRef}>
          <div className="flex h-full">
            {communities.map((community, index) => (
              <div key={community.id} className="relative flex-[0_0_100%] h-full min-w-0">
                <Image
                  src={community.image_url || ""}
                  alt={community.name}
                  fill
                  className="object-cover"
                  priority={index === 0}
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 90vw, 1400px"
                />
                {/* Subtle gradient overlay for better text visibility if needed, but bottom bar is opaque */}
                <div className="absolute inset-0 bg-black/5" />
              </div>
            ))}
          </div>
        </div>

        {/* Navigation Arrows - Adjusted to sit slightly outside or on edge */}
        <button
          onClick={scrollPrev}
          className="absolute -left-2 md:-left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center text-primary/80 hover:text-primary transition-colors bg-white/90 backdrop-blur-sm rounded-full shadow-xl border border-primary/10"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <button
          onClick={scrollNext}
          className="absolute -right-2 md:-right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center text-primary/80 hover:text-primary transition-colors bg-white/90 backdrop-blur-sm rounded-full shadow-xl border border-primary/10"
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        {/* Bottom Info Bar - Now positioned absolute WITHIN the image container at the bottom */}
        <div className="absolute bottom-0 left-0 right-0 w-full bg-white/95 backdrop-blur-md z-10 py-4 md:py-8 border-t border-gray-100 rounded-b-xl mx-auto">
          <div className="px-6 md:px-12">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-12">

              {/* Left: Title/Logo Area */}
              <div className="text-center md:text-left min-w-[200px]">
                <div className="flex flex-col items-center md:items-start">
                  {/* Mimicking a logo style */}
                  <span className="font-serif text-2xl md:text-3xl uppercase tracking-widest text-primary leading-none mb-1">
                    {communities[selectedIndex]?.name}
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                    {communities[selectedIndex]?.city}
                  </span>
                </div>
              </div>

              {/* Center: Amenities */}
              <div className="flex-1 flex justify-center gap-4 md:gap-16 border-t md:border-t-0 md:border-l md:border-r border-gray-200 py-3 md:py-0 w-full md:w-auto overflow-x-auto no-scrollbar">
                {communities[selectedIndex]?.highlights?.slice(0, 4).map((amenity, idx) => (
                  <div key={amenity} className="flex flex-col items-center text-center group/icon min-w-[60px] md:min-w-[80px]">
                    <div className="text-primary/60 group-hover/icon:text-primary transition-colors">
                      {getAmenityIcon(amenity)}
                    </div>
                    <span className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground">
                      {amenity}
                    </span>
                  </div>
                ))}
              </div>

              {/* Right: Button */}
              <div className="min-w-[150px] flex justify-center md:justify-end">
                <Link href="/communities">
                  <Button
                    variant="outline"
                    className="rounded-xl px-8 h-10 uppercase tracking-widest text-xs font-bold border-primary text-primary hover:bg-primary hover:text-white transition-all duration-300"
                  >
                    Explore All
                  </Button>
                </Link>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
